import { CircleAlertIcon, CircleCheckIcon } from 'lucide-react'

import { Progress } from '@ark-ui/react/progress'

import { formatBytes } from '@/utils/files'

type SharedProps = {
  fileName: string
}

export function CellUploadWidgetUploading({
  progress,
  fileName,
  fileSize,
}: SharedProps & {
  fileSize: number
  progress: number
}) {
  const loadedBytes = Math.round(fileSize * (progress / 100))
  const formattedSize = formatBytes(loadedBytes)
  const formattedTotalSize = formatBytes(fileSize)

  return (
    <Widget_Root>
      <Widget_Progress progress={progress === 0 ? null : progress} />
      <Widget_Content fileName={fileName}>
        <span>
          {progress}% ({formattedSize} of {formattedTotalSize})
        </span>
      </Widget_Content>
    </Widget_Root>
  )
}

export function CellUploadWidgetCompleted({
  fileName,
  fileSize,
}: SharedProps & { fileSize: number }) {
  const formattedTotalSize = formatBytes(fileSize)
  return (
    <Widget_Root>
      <CircleCheckIcon className="size-6 flex-none text-green-400" />
      <Widget_Content fileName={fileName}>
        <span>Uploaded ({formattedTotalSize})</span>
      </Widget_Content>
    </Widget_Root>
  )
}

export function CellUploadWidgetFailed({
  fileName,
  error,
}: SharedProps & { error: string }) {
  return (
    <Widget_Root>
      <CircleAlertIcon className="size-6 flex-none text-red-400" />
      <Widget_Content fileName={fileName}>
        <span>{error ?? 'Upload failed!'}</span>
      </Widget_Content>
    </Widget_Root>
  )
}

// =============== COMPOUND

function Widget_Root({ children }: React.ComponentProps<'div'>) {
  return (
    <div className="w-full my-2 flex items-center gap-1.5 py-1 px-2 bg-white/10 text-white rounded-md shadow-sm select-none">
      {children}
    </div>
  )
}

function Widget_Content({
  fileName,
  children,
}: React.ComponentProps<'div'> & { fileName: string }) {
  return (
    <div className="grow text-left leading-snug overflow-hidden">
      <div className="text-sm font-semibold truncate">{fileName}</div>
      <div className="text-muted-foreground text-xs truncate">{children}</div>
    </div>
  )
}

function Widget_Progress({ progress }: { progress: number | null }) {
  return (
    <Progress.Root
      value={progress}
      min={0}
      max={100}
    >
      <Progress.Circle className="[--size:1.75rem] [--thickness:0.25rem] data-[state=indeterminate]:animate-spin">
        <Progress.CircleTrack className="stroke-gray-700" />
        <Progress.CircleRange className="stroke-blue-500 data-[state=indeterminate]:[stroke-dasharray:10_100]" />
      </Progress.Circle>
    </Progress.Root>
  )
}
